import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShoppingCart, FileText, Package, LogOut,
  ChevronDown, ChevronUp, Check, X, Minus, Plus,
  TrendingUp, TrendingDown, DollarSign, RefreshCw,
  Trash2, Edit3, AlertCircle, ClipboardList, Megaphone,
  Eye, EyeOff, ToggleLeft, ToggleRight, Settings,
  Fuel, Flame, Droplets, Atom, Wrench, Wheat, Zap,
  ArrowRight, Hammer, Pickaxe, Factory, Truck, Anchor,
  Shield, Sword, Crosshair, Target, MapPin, Globe
} from 'lucide-react';
import { authAPI, ordersAPI, applicationsAPI, inventoryAPI, announcementsAPI, settingsAPI, homeProductsAPI } from '../api';
// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

function fmtDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status }) {
  return (
    <span className={`bce-badge bce-badge-${status}`}>
      {status}
    </span>
  );
}

// ─── ORDERS TAB ──────────────────────────────────────────────────────────────

function OrdersTab() {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter]     = useState('all');

  const load = useCallback(() => {
    ordersAPI.listMgmt()
      .then(r => setOrders(r.data))
      .catch(() => toast.error('Failed to load orders'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await ordersAPI.setStatus(id, status);
      setOrders(o => o.map(x => x.id === id ? { ...x, status } : x));
      toast.success(`Order marked as ${status}`);
    } catch { toast.error('Failed to update order'); }
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  const counts = {
    all: orders.length,
    pending:   orders.filter(o => o.status === 'pending').length,
    fulfilled: orders.filter(o => o.status === 'fulfilled').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading orders...</div>;

  return (
    <div>
      {/* Stats */}
      <div className="bce-stats-grid">
        <div className="bce-stat">
          <div className="bce-stat-val">{counts.all}</div>
          <div className="bce-stat-label">Total Orders</div>
        </div>
        <div className="bce-stat">
          <div className="bce-stat-val">{counts.pending}</div>
          <div className="bce-stat-label">Pending</div>
        </div>
        <div className="bce-stat">
          <div className="bce-stat-val">{counts.fulfilled}</div>
          <div className="bce-stat-label">Fulfilled</div>
        </div>
        <div className="bce-stat" style={{ borderTopColor: 'var(--bce-danger)' }}>
          <div className="bce-stat-val" style={{ color: 'var(--bce-danger)' }}>{counts.cancelled}</div>
          <div className="bce-stat-label">Cancelled</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '1px solid var(--bce-border)', paddingBottom: '1px' }}>
        {['all', 'pending', 'fulfilled', 'cancelled'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              background: 'transparent', border: 'none',
              fontFamily: 'var(--font-condensed)', fontSize: 12,
              fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase', cursor: 'pointer',
              color: filter === f ? 'var(--bce-gold)' : 'var(--bce-text-faint)',
              borderBottom: filter === f ? '2px solid var(--bce-gold)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}>
            {f} ({counts[f] ?? 0})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bce-empty">
          <div className="bce-empty-icon"><ShoppingCart size={36} /></div>
          <h3>No orders found</h3>
          <p style={{ fontSize: 13, color: 'var(--bce-text-faint)' }}>Orders submitted by customers will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
          {filtered.map(order => (
            <div key={order.id}>
              <div
                onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                style={{
                  background: expanded === order.id ? 'var(--bce-bg-elevated)' : 'var(--bce-bg-card)',
                  padding: '1rem 1.5rem',
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  cursor: 'pointer',
                  borderLeft: `3px solid ${order.status === 'fulfilled' ? 'var(--bce-success)' : order.status === 'cancelled' ? 'var(--bce-danger)' : 'var(--bce-gold)'}`,
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, color: 'var(--bce-text-faint)', minWidth: 36 }}>
                  #{order.id}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: 'var(--bce-white)', fontSize: 14 }}>{order.customer_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--bce-text-faint)' }}>{order.customer_phone}</div>
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--bce-gold)', minWidth: 90, textAlign: 'right' }}>
                  ${parseFloat(order.total_value || 0).toLocaleString()}
                </div>
                <StatusBadge status={order.status} />
                <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', minWidth: 60, textAlign: 'right' }}>
                  {timeAgo(order.created_at)}
                </div>
                {expanded === order.id ? <ChevronUp size={14} color="var(--bce-text-faint)" /> : <ChevronDown size={14} color="var(--bce-text-faint)" />}
              </div>

              <AnimatePresence>
                {expanded === order.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden', background: 'var(--bce-bg-elevated)', borderLeft: '3px solid var(--bce-gold)' }}
                  >
                    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                      {/* Items */}
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <div className="bce-label" style={{ marginBottom: 10 }}>Items Ordered</div>
                        {(Array.isArray(order.items) ? order.items : []).map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--bce-border)', fontSize: 13 }}>
                            <span style={{ color: 'var(--bce-text)' }}>{item.name} × {item.quantity}</span>
                            <span style={{ color: 'var(--bce-gold)', fontFamily: 'var(--font-display)' }}>
                              ${((item.price || 0) * item.quantity).toLocaleString()}
                            </span>
                          </div>
                        ))}
                        {order.notes && (
                          <div style={{ marginTop: '0.75rem', padding: '8px 12px', background: 'var(--bce-gold-dim)', border: '1px solid var(--bce-gold-border)', fontSize: 13, color: 'var(--bce-text)' }}>
                            <strong style={{ color: 'var(--bce-gold)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Notes:</strong> {order.notes}
                          </div>
                        )}
                      </div>
                      {/* Actions */}
                      {order.status === 'pending' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-start' }}>
                          <div className="bce-label" style={{ marginBottom: 2 }}>Actions</div>
                          <button className="bce-btn bce-btn-sm" onClick={() => updateStatus(order.id, 'fulfilled')}
                            style={{ background: 'var(--bce-success)', color: '#000', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Check size={13} /> Mark Fulfilled
                          </button>
                          <button className="bce-btn bce-btn-danger bce-btn-sm" onClick={() => updateStatus(order.id, 'cancelled')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <X size={13} /> Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── APPLICATIONS TAB ────────────────────────────────────────────────────────

function ApplicationsTab() {
  const [apps, setApps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter]     = useState('all');
  const [noteInput, setNote]    = useState('');

  useEffect(() => {
    applicationsAPI.list()
      .then(r => setApps(r.data))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await applicationsAPI.setStatus(id, status, noteInput || undefined);
      setApps(a => a.map(x => x.id === id ? { ...x, status } : x));
      setExpanded(null);
      setNote('');
      toast.success(`Application ${status}`);
    } catch { toast.error('Failed to update application'); }
  };

  const filtered = filter === 'all' ? apps : apps.filter(a => a.status === filter);
  const counts = { all: apps.length, pending: apps.filter(a => a.status === 'pending').length, approved: apps.filter(a => a.status === 'approved').length, denied: apps.filter(a => a.status === 'denied').length };

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading applications...</div>;

  return (
    <div>
      <div className="bce-stats-grid">
        <div className="bce-stat"><div className="bce-stat-val">{counts.all}</div><div className="bce-stat-label">Total</div></div>
        <div className="bce-stat"><div className="bce-stat-val">{counts.pending}</div><div className="bce-stat-label">Pending</div></div>
        <div className="bce-stat" style={{ borderTopColor: 'var(--bce-success)' }}><div className="bce-stat-val" style={{ color: 'var(--bce-success)' }}>{counts.approved}</div><div className="bce-stat-label">Approved</div></div>
        <div className="bce-stat" style={{ borderTopColor: 'var(--bce-danger)' }}><div className="bce-stat-val" style={{ color: 'var(--bce-danger)' }}>{counts.denied}</div><div className="bce-stat-label">Denied</div></div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '1px solid var(--bce-border)', paddingBottom: '1px' }}>
        {['all', 'pending', 'approved', 'denied'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 16px', background: 'transparent', border: 'none',
            fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 600,
            letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
            color: filter === f ? 'var(--bce-gold)' : 'var(--bce-text-faint)',
            borderBottom: filter === f ? '2px solid var(--bce-gold)' : '2px solid transparent',
            transition: 'all 0.15s',
          }}>
            {f} ({counts[f] ?? 0})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bce-empty">
          <div className="bce-empty-icon"><FileText size={36} /></div>
          <h3>No applications</h3>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
          {filtered.map(app => (
            <div key={app.id}>
              <div onClick={() => setExpanded(expanded === app.id ? null : app.id)}
                style={{
                  background: expanded === app.id ? 'var(--bce-bg-elevated)' : 'var(--bce-bg-card)',
                  padding: '1rem 1.5rem',
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  cursor: 'pointer',
                  borderLeft: `3px solid ${app.status === 'approved' ? 'var(--bce-success)' : app.status === 'denied' ? 'var(--bce-danger)' : 'var(--bce-gold)'}`,
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, color: 'var(--bce-white)', fontSize: 14 }}>{app.in_game_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--bce-text-faint)' }}>{app.discord_username}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--bce-text-faint)' }}>Age {app.age} · {app.timezone}</div>
                <StatusBadge status={app.status} />
                <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', minWidth: 60, textAlign: 'right' }}>{timeAgo(app.created_at)}</div>
                {expanded === app.id ? <ChevronUp size={14} color="var(--bce-text-faint)" /> : <ChevronDown size={14} color="var(--bce-text-faint)" />}
              </div>

              <AnimatePresence>
                {expanded === app.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden', background: 'var(--bce-bg-elevated)', borderLeft: '3px solid var(--bce-gold)' }}>
                    <div style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.25rem' }}>
                        {[
                          ['Availability', app.availability],
                          ['Submitted', fmtDate(app.created_at)],
                        ].map(([label, val]) => (
                          <div key={label}><div className="bce-label" style={{ marginBottom: 4 }}>{label}</div><div style={{ color: 'var(--bce-text)', fontSize: 14 }}>{val}</div></div>
                        ))}
                      </div>
                      {[['Experience', app.experience], ['Why Join', app.why_join]].map(([label, val]) => (
                        <div key={label} style={{ marginBottom: '1rem' }}>
                          <div className="bce-label" style={{ marginBottom: 4 }}>{label}</div>
                          <div style={{ color: 'var(--bce-text)', fontSize: 14, lineHeight: 1.7, background: 'var(--bce-bg-card)', padding: '10px 12px', border: '1px solid var(--bce-border)' }}>{val}</div>
                        </div>
                      ))}
                      {app.notes && (
                        <div style={{ marginBottom: '1rem', padding: '10px 12px', background: 'var(--bce-gold-dim)', border: '1px solid var(--bce-gold-border)', fontSize: 13, color: 'var(--bce-text)' }}>
                          <strong style={{ color: 'var(--bce-gold)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Review note:</strong> {app.notes}
                        </div>
                      )}
                      {app.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid var(--bce-border)', paddingTop: '1rem' }}>
                          <div className="bce-field" style={{ flex: 1, minWidth: 200 }}>
                            <label>Optional note</label>
                            <input className="bce-input" value={noteInput} onChange={e => setNote(e.target.value)} style={{ padding: '8px 12px' }} />
                          </div>
                          <button className="bce-btn bce-btn-sm" onClick={() => updateStatus(app.id, 'approved')}
                            style={{ background: 'var(--bce-success)', color: '#000', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Check size={13} /> Approve
                          </button>
                          <button className="bce-btn bce-btn-danger bce-btn-sm" onClick={() => updateStatus(app.id, 'denied')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <X size={13} /> Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ADJUST MODAL ─────────────────────────────────────────────────────────────

function AdjustModal({ product, onClose, onDone }) {
  const [delta, setDelta]       = useState(0);
  const [reason, setReason]     = useState('');
  const [cashflow, setCashflow] = useState('');
  const [busy, setBusy]         = useState(false);
  const [mode, setMode]         = useState('add'); // 'add'|'remove'

  const actualDelta = mode === 'add' ? Math.abs(delta) : -Math.abs(delta);
  const newQty = Math.max(0, product.quantity + actualDelta);

  const handleSubmit = async () => {
    if (!delta || delta <= 0) { toast.error('Enter a quantity'); return; }
    setBusy(true);
    try {
      await inventoryAPI.adjust(product.id, actualDelta, reason, parseFloat(cashflow) || 0);
      toast.success(`${product.name} updated`);
      onDone(newQty);
      onClose();
    } catch { toast.error('Failed to adjust'); } finally { setBusy(false); }
  };

  return (
    <div className="bce-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="bce-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div className="bce-modal-title">Adjust Stock</div>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 14, color: 'var(--bce-text)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
          {product.name}
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem' }}>
          {['add', 'remove'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`bce-btn bce-btn-sm ${mode === m ? 'bce-btn-gold' : 'bce-btn-ghost'}`}
              style={{ flex: 1, justifyContent: 'center' }}>
              {m === 'add' ? <><Plus size={13} /> Add Stock</> : <><Minus size={13} /> Remove Stock</>}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="bce-field">
            <label>Quantity *</label>
            <input className="bce-input" type="number" min="1" value={delta || ''}
              onChange={e => setDelta(parseInt(e.target.value) || 0)} />
          </div>
          <div className="bce-field">
            <label>Cashflow amount (optional)</label>
            <input className="bce-input" type="number" placeholder="e.g. 5000"
              value={cashflow} onChange={e => setCashflow(e.target.value)} />
          </div>
          <div className="bce-field">
            <label>Reason (optional)</label>
            <input className="bce-input" value={reason} onChange={e => setReason(e.target.value)} />
          </div>
        </div>

        {/* Preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bce-bg)', border: '1px solid var(--bce-border)', marginBottom: '1.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="bce-label">Current</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--bce-text)' }}>{product.quantity}</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: mode === 'add' ? 'var(--bce-success)' : 'var(--bce-danger)' }}>
              {mode === 'add' ? '+' : '-'}{delta || 0}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="bce-label">After</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--bce-gold)' }}>{newQty}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bce-btn bce-btn-gold" onClick={handleSubmit} disabled={busy} style={{ flex: 1, justifyContent: 'center' }}>
            {busy ? 'Saving...' : 'Confirm'}
          </button>
          <button className="bce-btn bce-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── PRODUCT FORM MODAL ───────────────────────────────────────────────────────

function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState({ name: product?.name || '', price: product?.price || '', unit: product?.unit || 'units', description: product?.description || '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.price) { toast.error('Name and price required'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await inventoryAPI.updateProduct(product.id, { ...form, price: parseFloat(form.price) });
      } else {
        await inventoryAPI.createProduct({ ...form, price: parseFloat(form.price) });
      }
      toast.success(isEdit ? 'Product updated' : 'Product added');
      onSaved();
      onClose();
    } catch { toast.error('Failed to save product'); } finally { setBusy(false); }
  };

  return (
    <div className="bce-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div className="bce-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div className="bce-modal-title">{isEdit ? 'Edit Product' : 'Add Product'}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="bce-field"><label>Name *</label><input className="bce-input" value={form.name} onChange={e => set('name', e.target.value)} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="bce-field"><label>Price *</label><input className="bce-input" type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} /></div>
            <div className="bce-field"><label>Unit</label><input className="bce-input" value={form.unit} onChange={e => set('unit', e.target.value)} /></div>
          </div>
          <div className="bce-field"><label>Description</label><input className="bce-input" value={form.description} onChange={e => set('description', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bce-btn bce-btn-gold" onClick={handleSubmit} disabled={busy} style={{ flex: 1, justifyContent: 'center' }}>
            {busy ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Product'}
          </button>
          <button className="bce-btn bce-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── INVENTORY TAB ────────────────────────────────────────────────────────────

function InventoryTab({ isGM = false }) {
  const [products, setProducts]   = useState([]);
  const [logs, setLogs]           = useState([]);
  const [cashflow, setCashflow]   = useState({ total_in: 0, total_out: 0, net: 0 });
  const [loading, setLoading]     = useState(true);
  const [adjustItem, setAdjust]   = useState(null);
  const [editItem, setEdit]       = useState(null);
  const [showAddModal, setAdd]    = useState(false);
  const [showLogs, setShowLogs]   = useState(false);

  const load = useCallback(async () => {
    try {
      const requests = [inventoryAPI.listProducts(), inventoryAPI.getLogs()];
      if (isGM) requests.push(inventoryAPI.getCashflow());
      const [pRes, lRes, cRes] = await Promise.all(requests);
      setProducts(pRes.data);
      setLogs(lRes.data);
      if (isGM && cRes) setCashflow(cRes.data);
    } catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  }, [isGM]);

  useEffect(() => { load(); }, [load]);

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await inventoryAPI.deleteProduct(id);
      setProducts(p => p.filter(x => x.id !== id));
      toast.success('Product deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const updateQty = (id, qty) => setProducts(p => p.map(x => x.id === id ? { ...x, quantity: qty } : x));

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading inventory...</div>;

  return (
    <div>
      {/* Stats — cashflow only visible to GM */}
      <div className="bce-stats-grid">
        {isGM ? (
          <>
            <div className="bce-stat" style={{ borderTopColor: 'var(--bce-success)' }}>
              <div className="bce-stat-val" style={{ color: 'var(--bce-success)' }}>${cashflow.total_in.toLocaleString()}</div>
              <div className="bce-stat-label">Cash In</div>
            </div>
            <div className="bce-stat" style={{ borderTopColor: 'var(--bce-danger)' }}>
              <div className="bce-stat-val" style={{ color: 'var(--bce-danger)' }}>${Math.abs(cashflow.total_out).toLocaleString()}</div>
              <div className="bce-stat-label">Cash Out</div>
            </div>
            <div className="bce-stat">
              <div className="bce-stat-val" style={{ color: cashflow.net >= 0 ? 'var(--bce-success)' : 'var(--bce-danger)' }}>
                ${cashflow.net.toLocaleString()}
              </div>
              <div className="bce-stat-label">Net Cashflow</div>
            </div>
          </>
        ) : (
          <div className="bce-stat" style={{ gridColumn: 'span 3' }}>
            <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Cashflow data visible to GM only
            </div>
          </div>
        )}
        <div className="bce-stat">
          <div className="bce-stat-val">{products.length}</div>
          <div className="bce-stat-label">Products</div>
        </div>
      </div>

      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div className="bce-section-label" style={{ marginBottom: 0, flex: 1 }}><span>Stock Levels</span></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={12} /> Refresh
          </button>
          <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => setShowLogs(!showLogs)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClipboardList size={12} /> {showLogs ? 'Hide Log' : 'View Log'}
          </button>
          <button className="bce-btn bce-btn-gold bce-btn-sm" onClick={() => setAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={12} /> Add Product
          </button>
        </div>
      </div>

      {/* Products */}
      {products.length === 0 ? (
        <div className="bce-empty">
          <div className="bce-empty-icon"><Package size={36} /></div>
          <h3>No products</h3>
          <p style={{ fontSize: 13, color: 'var(--bce-text-faint)', marginBottom: '1rem' }}>Add your first product to start tracking inventory.</p>
          <button className="bce-btn bce-btn-gold" onClick={() => setAdd(true)}>Add Product</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)', marginBottom: '2rem' }}>
          {products.map(p => (
            <div key={p.id} className="bce-product-card">
              <div style={{ flex: 1 }}>
                <div className="bce-product-name">{p.name}</div>
                <div className="bce-product-price">${parseFloat(p.price).toLocaleString()} / {p.unit}</div>
                {p.description && <div style={{ fontSize: 12, color: 'var(--bce-text-faint)', marginTop: 2 }}>{p.description}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="bce-product-qty">{p.quantity}</div>
                <div className="bce-product-qty-label">{p.unit} in stock</div>
              </div>
              <div className="bce-product-actions">
                <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => setAdjust(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Plus size={12} /><Minus size={12} />
                </button>
                <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => setEdit(p)}>
                  <Edit3 size={12} />
                </button>
                <button className="bce-btn bce-btn-danger bce-btn-sm" onClick={() => deleteProduct(p.id, p.name)}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity log */}
      <AnimatePresence>
        {showLogs && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            <div className="bce-section-label" style={{ marginBottom: '1rem' }}><span>Activity Log</span></div>
            {logs.length === 0 ? (
              <div style={{ color: 'var(--bce-text-faint)', fontSize: 13 }}>No activity recorded yet.</div>
            ) : (
              <div style={{ background: 'var(--bce-bg-card)', border: '1px solid var(--bce-border)', padding: '1rem 1.5rem' }}>
                {logs.slice(0, 50).map(log => {
                  const isIn = (log.delta || 0) > 0;
                  return (
                    <div key={log.id} className="bce-log-entry">
                      <div className={`bce-log-dot ${isIn ? 'in' : 'out'}`} />
                      <div className="bce-log-product">{log.product_name}</div>
                      <div className={`bce-log-delta ${isIn ? 'in' : 'out'}`}>
                        {isIn ? '+' : ''}{log.delta}
                      </div>
                      {log.cashflow !== 0 && (
                        <div style={{ fontSize: 12, color: log.cashflow > 0 ? 'var(--bce-success)' : 'var(--bce-danger)' }}>
                          {log.cashflow > 0 ? '+' : ''}${log.cashflow.toLocaleString()}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--bce-text-faint)', flex: 1 }}>{log.reason || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--bce-text-faint)' }}>{log.user_name}</div>
                      <div className="bce-log-time">{timeAgo(log.created_at)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      {adjustItem && <AdjustModal product={adjustItem} onClose={() => setAdjust(null)} onDone={qty => updateQty(adjustItem.id, qty)} />}
      {editItem   && <ProductModal product={editItem} onClose={() => setEdit(null)} onSaved={load} />}
      {showAddModal && <ProductModal onClose={() => setAdd(false)} onSaved={load} />}
    </div>
  );
}

// ─── ANNOUNCEMENTS TAB ────────────────────────────────────────────────────────

const BADGE_OPTIONS = [
  { value: 'news',     label: 'News',      color: '#8888ff' },
  { value: 'restock',  label: 'Restock',   color: '#22c55e' },
  { value: 'discount', label: 'Discount',  color: '#F5A800' },
  { value: 'storm',    label: 'Storm Sale',color: '#ef4444' },
  { value: 'alert',    label: 'Alert',     color: '#F5A800' },
];

function AnnouncementsTab() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]     = useState({ title: '', body: '', badge: 'news' });
  const [busy, setBusy]     = useState(false);

  const load = useCallback(() => {
    announcementsAPI.listAll()
      .then(r => setItems(r.data))
      .catch(() => toast.error('Failed to load announcements'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      await announcementsAPI.create(form);
      toast.success('Announcement posted');
      setForm({ title: '', body: '', badge: 'news' });
      setShowForm(false);
      load();
    } catch { toast.error('Failed to post'); } finally { setBusy(false); }
  };

  const toggleActive = async (ann) => {
    try {
      await announcementsAPI.update(ann.id, { is_active: !ann.is_active });
      setItems(prev => prev.map(a => a.id === ann.id ? { ...a, is_active: !a.is_active } : a));
      toast.success(ann.is_active ? 'Announcement hidden' : 'Announcement published');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await announcementsAPI.delete(id);
      setItems(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div className="bce-section-label" style={{ marginBottom: 0 }}><span>Announcements</span></div>
        <button
          className={`bce-btn bce-btn-sm ${showForm ? 'bce-btn-ghost' : 'bce-btn-gold'}`}
          onClick={() => setShowForm(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {showForm ? <><X size={12} /> Cancel</> : <><Plus size={12} /> New Post</>}
        </button>
      </div>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            onSubmit={handleCreate}
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden', marginBottom: '1.5rem' }}
          >
            <div style={{
              background: 'var(--bce-bg-elevated)',
              border: '1px solid var(--bce-border)',
              borderTop: '2px solid var(--bce-gold)',
              padding: '1.5rem',
              display: 'flex', flexDirection: 'column', gap: '1rem',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
                <div className="bce-field">
                  <label>Title *</label>
                  <input className="bce-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Restocking Friday — Jerry Cans & Ethanol" />
                </div>
                <div className="bce-field">
                  <label>Type</label>
                  <select className="bce-select" value={form.badge} onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} style={{ minWidth: 130 }}>
                    {BADGE_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="bce-field">
                <label>Body (optional)</label>
                <textarea className="bce-textarea" rows={2} value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="Additional details visible on the homepage..."
                  style={{ minHeight: 60 }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="bce-btn bce-btn-gold bce-btn-sm" disabled={busy}>
                  {busy ? 'Posting...' : 'Post Announcement'}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* List */}
      {items.length === 0 ? (
        <div className="bce-empty">
          <div className="bce-empty-icon"><Megaphone size={36} /></div>
          <h3>No announcements</h3>
          <p style={{ fontSize: 13, color: 'var(--bce-text-faint)' }}>Post your first announcement to show it on the homepage.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
          {items.map(ann => {
            const badge = BADGE_OPTIONS.find(b => b.value === ann.badge) || BADGE_OPTIONS[0];
            return (
              <div key={ann.id} style={{
                background: 'var(--bce-bg-card)', padding: '1rem 1.5rem',
                display: 'flex', alignItems: 'flex-start', gap: '1rem',
                borderLeft: `3px solid ${ann.is_active ? badge.color : 'transparent'}`,
                opacity: ann.is_active ? 1 : 0.5,
                transition: 'all 0.15s',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 8, fontFamily: 'var(--font-condensed)', fontWeight: 700,
                      letterSpacing: '0.15em', textTransform: 'uppercase',
                      color: badge.color, border: `1px solid ${badge.color}33`,
                      padding: '1px 6px',
                    }}>
                      {badge.label}
                    </span>
                    <span style={{
                      fontFamily: 'var(--font-condensed)', fontWeight: 700,
                      fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: 'var(--bce-white)',
                    }}>
                      {ann.title}
                    </span>
                  </div>
                  {ann.body && (
                    <div style={{ fontSize: 12, color: 'var(--bce-text)', lineHeight: 1.6 }}>{ann.body}</div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', marginTop: 4 }}>
                    By {ann.created_by} · {new Date(ann.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {!ann.is_active && <span style={{ marginLeft: 8, color: 'var(--bce-danger)' }}>· Hidden</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    className="bce-btn bce-btn-ghost bce-btn-sm"
                    onClick={() => toggleActive(ann)}
                    title={ann.is_active ? 'Hide' : 'Publish'}
                    style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    {ann.is_active ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button
                    className="bce-btn bce-btn-danger bce-btn-sm"
                    onClick={() => handleDelete(ann.id)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── DISCORD SETTINGS TAB (GM only) ──────────────────────────────────────────

function DiscordSettingsTab() {
  const [form, setForm]     = useState({
    discord_orders_channel:       '',
    discord_inventory_channel:    '',
    discord_applications_channel: '',
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    settingsAPI.getDiscord()
      .then(r => setForm(r.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await settingsAPI.updateDiscord(form);
      toast.success('Discord settings saved');
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.status || e.message || 'unknown';
      toast.error(`Failed to save — ${msg}`);
    } finally { setBusy(false); }
  };

  const Field = ({ label, description, field }) => (
    <div className="bce-field">
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--bce-text-faint)', fontWeight: 400, letterSpacing: '0.02em', textTransform: 'none' }}>
          {description}
        </span>
      </label>
      <input
        className="bce-input"
        value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder="Channel ID  e.g. 1504512809009025127"
        style={{ fontFamily: 'monospace', fontSize: 13 }}
      />
    </div>
  );

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div className="bce-section-label" style={{ marginBottom: 6 }}><span>Discord Notifications</span></div>
        <p style={{ fontSize: 13, color: 'var(--bce-text-faint)', lineHeight: 1.6 }}>
          Set the Discord channel IDs where notifications will be posted. Right-click a channel → Copy Channel ID.
        </p>
      </div>

      <form onSubmit={handleSave}>
        <div style={{
          background: 'var(--bce-bg-card)',
          border: '1px solid var(--bce-border)',
          borderTop: '2px solid var(--bce-gold)',
          padding: '1.75rem',
          display: 'flex', flexDirection: 'column', gap: '1.5rem',
        }}>

          <Field
            label="Orders Channel"
            description="New order document image posted here when a customer submits an order."
            field="discord_orders_channel"
          />
          <Field
            label="Inventory Channel"
            description="Stock adjustments (restock/consume) and low stock alerts posted here."
            field="discord_inventory_channel"
          />
          <Field
            label="Applications Channel"
            description="New job applications posted here when someone submits the Apply form."
            field="discord_applications_channel"
          />

          <div style={{ paddingTop: '0.5rem', borderTop: '1px solid var(--bce-border)', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="bce-btn bce-btn-gold" disabled={busy}>
              {busy ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}


// ─── HOME PRODUCTS TAB ───────────────────────────────────────────────────────

const HOME_ICON_OPTIONS = [
  "Fuel","Flame","Droplets","Atom","Wrench","Wheat","Zap","Package",
  "ShoppingCart","FileText","LogOut","ChevronDown","ChevronUp",
  "Check","X","Minus","Plus","TrendingUp","TrendingDown","DollarSign",
  "RefreshCw","Trash2","Edit3","AlertCircle","ClipboardList","Megaphone",
  "Eye","EyeOff","ToggleLeft","ToggleRight","Settings","ArrowRight",
  "Hammer","Pickaxe","Factory","Truck","Anchor","Shield","Sword",
  "Crosshair","Target","MapPin","Globe"
];

function HomeProductsTab() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ name: '', price: '', unit: 'units', category: 'buy', icon: 'Package', sort_order: 0 });
  const [busy, setBusy]       = useState(false);
  const [filter, setFilter]   = useState('all');

  const load = useCallback(() => {
    homeProductsAPI.listAll()
      .then(r => setItems(r.data))
      .catch(() => toast.error('Failed to load home products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: '', price: '', unit: 'units', category: 'buy', icon: 'Package', sort_order: 0 });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) { toast.error('Name and price required'); return; }
    setBusy(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), sort_order: parseInt(form.sort_order) || 0 };
      if (editing) {
        await homeProductsAPI.update(editing.id, payload);
        toast.success('Product updated');
      } else {
        await homeProductsAPI.create(payload);
        toast.success('Product added');
      }
      resetForm();
      setShowForm(false);
      load();
    } catch { toast.error('Failed to save'); } finally { setBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await homeProductsAPI.delete(id);
      setItems(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleEdit = (p) => {
    setForm({ name: p.name, price: p.price, unit: p.unit, category: p.category, icon: p.icon, sort_order: p.sort_order });
    setEditing(p);
    setShowForm(true);
  };

  const filtered = filter === 'all' ? items : items.filter(p => p.category === filter);
  const counts = {
    all: items.length,
    buy: items.filter(p => p.category === 'buy').length,
    sell: items.filter(p => p.category === 'sell').length,
  };

  const getIconComp = (name) => {
    const map = { Fuel, Flame, Droplets, Atom, Wrench, Wheat, Zap, Package,
      ShoppingCart, FileText, LogOut, ChevronDown, ChevronUp,
      Check, X, Minus, Plus, TrendingUp, TrendingDown, DollarSign,
      RefreshCw, Trash2, Edit3, AlertCircle, ClipboardList, Megaphone,
      Eye, EyeOff, ToggleLeft, ToggleRight, Settings, ArrowRight,
      Hammer, Pickaxe, Factory, Truck, Anchor, Shield, Sword,
      Crosshair, Target, MapPin, Globe
    };
    return map[name] || Package;
  };

  if (loading) return <div className="bce-loading"><div className="bce-spinner" /> Loading home products...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="bce-section-label" style={{ marginBottom: 0 }}><span>Home Products</span></div>
        <button
          className={`bce-btn bce-btn-sm ${showForm ? 'bce-btn-ghost' : 'bce-btn-gold'}`}
          onClick={() => { if (showForm) { setShowForm(false); resetForm(); } else setShowForm(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {showForm ? <><X size={12} /> Cancel</> : <><Plus size={12} /> Add Product</>}
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', borderBottom: '1px solid var(--bce-border)', paddingBottom: '1px' }}>
        {['all', 'buy', 'sell'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px', background: 'transparent', border: 'none',
              fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 600,
              letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
              color: filter === f ? 'var(--bce-gold)' : 'var(--bce-text-faint)',
              borderBottom: filter === f ? '2px solid var(--bce-gold)' : '2px solid transparent',
              transition: 'all 0.15s',
            }}>
            {f} ({counts[f] || 0})
          </button>
        ))}
      </div>

      {/* Create/Edit form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden', marginBottom: '1.5rem' }}
          >
            <div style={{
              background: 'var(--bce-bg-elevated)',
              border: '1px solid var(--bce-border)',
              borderTop: '2px solid var(--bce-gold)',
              padding: '1.5rem',
              display: 'flex', flexDirection: 'column', gap: '1rem',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="bce-field">
                  <label>Name *</label>
                  <input className="bce-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Jerry Cans" />
                </div>
                <div className="bce-field">
                  <label>Price *</label>
                  <input className="bce-input" type="number" min="0" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} placeholder="1000" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div className="bce-field">
                  <label>Category</label>
                  <select className="bce-select" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    <option value="buy">Buy (We Buy)</option>
                    <option value="sell">Sell (We Sell)</option>
                  </select>
                </div>
                <div className="bce-field">
                  <label>Unit</label>
                  <input className="bce-input" value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))} placeholder="units" />
                </div>
                <div className="bce-field">
                  <label>Sort Order</label>
                  <input className="bce-input" type="number" value={form.sort_order} onChange={e => setForm(f => ({...f, sort_order: e.target.value}))} placeholder="0" />
                </div>
              </div>
              <div className="bce-field">
                <label>Icon (Lucide)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto', padding: '8px', background: 'var(--bce-bg-card)', border: '1px solid var(--bce-border)' }}>
                  {HOME_ICON_OPTIONS.map(iconName => {
                    const IconComp = getIconComp(iconName);
                    const isActive = form.icon === iconName;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setForm(f => ({...f, icon: iconName}))}
                        title={iconName}
                        style={{
                          width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: isActive ? 'var(--bce-gold)' : 'transparent',
                          border: `1px solid ${isActive ? 'var(--bce-gold)' : 'var(--bce-border)'}`,
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <IconComp size={16} color={isActive ? '#000' : 'var(--bce-gold)'} />
                      </button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 11, color: 'var(--bce-text-faint)', marginTop: 4 }}>Selected: <strong style={{ color: 'var(--bce-gold)' }}>{form.icon}</strong></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
                <button type="submit" className="bce-btn bce-btn-gold bce-btn-sm" disabled={busy}>
                  {busy ? 'Saving...' : (editing ? 'Update Product' : 'Add Product')}
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bce-empty">
          <div className="bce-empty-icon"><Package size={36} /></div>
          <h3>No products</h3>
          <p style={{ fontSize: 13, color: 'var(--bce-text-faint)' }}>Add products to show on the homepage.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--bce-border)' }}>
          {filtered.map(p => {
            const IconComp = getIconComp(p.icon);
            return (
              <div key={p.id} style={{
                background: 'var(--bce-bg-card)', padding: '1rem 1.5rem',
                display: 'flex', alignItems: 'center', gap: '1rem',
                borderLeft: `3px solid ${p.category === 'buy' ? 'var(--bce-success)' : 'var(--bce-gold)'}`,
                transition: 'all 0.15s',
              }}>
                <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bce-gold-dim)', border: '1px solid var(--bce-gold-border)' }}>
                  <IconComp size={18} color="var(--bce-gold)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--bce-white)' }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--bce-text-faint)', marginTop: 2 }}>
                    {p.category.toUpperCase()} &middot; ${parseFloat(p.price).toLocaleString()} / {p.unit} &middot; Order: {p.sort_order}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="bce-btn bce-btn-ghost bce-btn-sm" onClick={() => handleEdit(p)} title="Edit">
                    <Edit3 size={12} />
                  </button>
                  <button className="bce-btn bce-btn-danger bce-btn-sm" onClick={() => handleDelete(p.id)} title="Delete">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── DASHBOARD ROOT ───────────────────────────────────────────────────────────

const ALL_NAV = [
  { key: 'orders',        label: 'Orders',        icon: ShoppingCart, roles: ['manager', 'gm'] },
  { key: 'applications',  label: 'Applications',  icon: FileText,     roles: ['manager', 'gm'] },
  { key: 'inventory',     label: 'Inventory',     icon: Package,      roles: ['manager', 'gm'] },
  { key: 'home-products', label: 'Home Products', icon: Package,      roles: ['manager', 'gm'] },
  { key: 'announcements', label: 'Announcements', icon: Megaphone,    roles: ['manager', 'gm'] },
  { key: 'settings',      label: 'Settings',      icon: Settings,     roles: ['gm'] },
];

const ROLE_LABELS = {
  gm:      { label: 'General Manager', color: 'var(--bce-gold)' },
  manager: { label: 'Manager',         color: '#FFB800'         },
  employee:{ label: 'Employee',        color: 'var(--bce-text)' },
  none:    { label: 'Staff',           color: 'var(--bce-text-faint)' },
};

export default function Dashboard() {
  const [user, setUser]   = useState(null);
  const [tab, setTab]     = useState('orders');
  const navigate          = useNavigate();

  useEffect(() => {
    if (process.env.REACT_APP_DEV_MODE === 'true') {
      setUser({
        discord_id: 'dev', username: 'DevUser', global_name: 'Developer',
        is_management: true, role_level: 'gm', avatar_url: null,
      });
      return;
    }
    authAPI.getUserProfile()
      .then(r => {
        if (!r.data.is_management) { navigate('/'); return; }
        setUser(r.data);
      })
      .catch(() => navigate('/'));
  }, [navigate]);

  const handleLogout = () => { localStorage.removeItem('bce_token'); navigate('/'); };

  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="bce-loading"><div className="bce-spinner" style={{ width: 32, height: 32 }} /></div>
    </div>
  );

  const roleLevel = user.role_level || (user.is_management ? 'manager' : 'none');
  const roleMeta  = ROLE_LABELS[roleLevel] || ROLE_LABELS.none;
  const isGM      = roleLevel === 'gm';

  // Filter nav items visible to this role
  const nav = ALL_NAV.filter(n => n.roles.includes(roleLevel));

  // Ensure current tab is valid for this role
  const activeTab = nav.find(n => n.key === tab) ? tab : (nav[0]?.key || 'orders');

  return (
    <div className="bce-dash-wrap">
      {/* Sidebar */}
      <aside className="bce-dash-sidebar">
        {/* User */}
        <div className="bce-sidebar-user">
          {user.avatar_url
            ? <img src={user.avatar_url} alt={user.username} className="bce-sidebar-avatar" />
            : <div className="bce-sidebar-avatar" style={{
                background: 'var(--bce-gold-dim)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)', color: 'var(--bce-gold)', fontSize: '1.2rem',
              }}>
                {user.username?.[0]?.toUpperCase()}
              </div>
          }
          <div className="bce-sidebar-name">{user.global_name || user.username}</div>
          <div className="bce-sidebar-role" style={{ color: roleMeta.color }}>{roleMeta.label}</div>
        </div>

        {/* Nav */}
        <nav>
          <ul className="bce-sidebar-nav">
            {nav.map(n => {
              const Icon = n.icon;
              return (
                <li key={n.key}>
                  <button className={activeTab === n.key ? 'active' : ''} onClick={() => setTab(n.key)}>
                    <Icon size={15} />
                    {n.label}
                  </button>
                </li>
              );
            })}
          </ul>

          <div style={{ margin: '1.5rem 1rem 0', borderTop: '1px solid var(--bce-border)', paddingTop: '1rem' }}>
            <button onClick={handleLogout}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', background: 'transparent', border: 'none',
                color: 'var(--bce-text-faint)', fontFamily: 'var(--font-condensed)',
                fontSize: 12, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase', cursor: 'pointer',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--bce-danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--bce-text-faint)'}
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <main className="bce-dash-content">
        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--bce-white)', letterSpacing: '0.04em' }}>BIG COUNTRY</span>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--bce-gold)', letterSpacing: '0.04em' }}>ENERGY</span>
            </div>
            <div className="bce-label">Management Dashboard</div>
          </div>
          {/* Role chip */}
          <div style={{
            padding: '4px 12px',
            border: `1px solid ${roleMeta.color}44`,
            fontFamily: 'var(--font-condensed)', fontSize: 11,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: roleMeta.color,
          }}>
            {roleMeta.label}
          </div>
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}
          >
            {activeTab === 'orders'        && <OrdersTab />}
            {activeTab === 'applications'  && <ApplicationsTab />}
            {activeTab === 'inventory'     && <InventoryTab isGM={isGM} />}
            {activeTab === 'home-products' && <HomeProductsTab />}
            {activeTab === 'announcements' && <AnnouncementsTab />}
            {activeTab === 'settings'      && <DiscordSettingsTab />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
